import type { Product, ProductCategory } from "@features/shopping/types";

import { useAuthStore } from "@features/authentication/stores";
import { productService } from "@features/shopping/api";
import { handleApiError } from "@shared/utils/error";
import { toast } from "@ui/toast";
import { defineStore } from "pinia";

type ProductState = {
  categories: ProductCategory[];
  error: null | string;
  loading: boolean;
  products: null | Product[];
};

export const useProductStore = defineStore("product", {
  actions: {
    clearError() {
      this.error = null;
    },

    async fetchCategories(countryCode?: string) {
      this.loading = true;
      this.error = null;

      try {
        const response = await productService.getProductCategories(countryCode);

        if (!response.success) {
          this.error = response.message;
          return null;
        }

        this.categories = response.product_categorys;

        return response;
      } catch (err) {
        const errorMessage = handleApiError(err, "Failed to fetch product categories");
        this.error = errorMessage;
        toast({
          description: errorMessage,
          title: "Error",
          variant: "destructive",
        });
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async fetchProducts(countryCode?: string) {
      this.loading = true;
      this.error = null;

      try {
        const response = await productService.getProducts({
          countryCode,
          memberId: this.getMemberId,
        });

        if (!response.success) {
          this.error = response.message;
          return null;
        }

        this.products = response.products;

        return response;
      } catch (err) {
        const errorMessage = handleApiError(err, "Failed to fetch products");
        this.error = errorMessage;
        toast({
          description: errorMessage,
          title: "Error",
          variant: "destructive",
        });
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async initializeProducts(countryCode?: string) {
      this.loading = true;
      this.error = null;

      try {
        const [categoriesResult, productsResult] = await Promise.allSettled([
          this.fetchCategories(countryCode),
          this.fetchProducts(countryCode),
        ]);

        if (categoriesResult.status === "rejected") {
          console.error("[Product Store] Categories fetch failed:", categoriesResult.reason);
          const errorMessage = handleApiError(
            categoriesResult.reason,
            "Failed to fetch categories",
          );
          this.error = errorMessage;
          // Don't toast here as individual methods already toast
        }

        if (productsResult.status === "rejected") {
          console.error("[Product Store] Products fetch failed:", productsResult.reason);
          const errorMessage = handleApiError(productsResult.reason, "Failed to fetch products");
          this.error = errorMessage;
          // Don't toast here as individual methods already toast
          throw productsResult.reason;
        }
      } catch (err) {
        const errorMessage = handleApiError(err, "Failed to initialize products");
        this.error = errorMessage;
        // Don't toast initialization errors as they're already toasted by individual methods
        throw err;
      } finally {
        this.loading = false;
      }
    },
  },

  getters: {
    getCategories: (state) => state.categories,
    getMemberId: () => {
      const authStore = useAuthStore();
      if (authStore.isAuthenticated && authStore.currentUser?.member_id) {
        return authStore.currentUser.member_id;
      }
      return ``;
    },
    getProducts: (state) => state.products,
  },

  state: (): ProductState => ({
    categories: [],
    error: null,
    loading: false,
    products: null,
  }),
});
